/**
 * Why a chat frame is read-only. Frame metadata stores a code (backend
 * lifecycle reasons are codes too) and the frame puts it into words when it
 * renders, so the text follows the app language. Server messages and reasons
 * stored before codes existed are not codes and render as stored.
 */
import { translate } from "@/i18n";
import { libChatMessages } from "@/i18n/messages/lib-chat";

const REASON_KEY = {
  agent_unavailable: "agentUnavailable",
  project_unavailable: "projectUnavailable",
  runtime_unavailable: "runtimeUnavailable",
  invalid_status_projection: "chatStatusUnavailable",
  direct_session_closed: "chatClosedInCodingApp",
  chat_unavailable: "chatUnavailable",
  chat_agent_unavailable: "chatAgentUnavailable",
  chat_project_unavailable: "chatProjectUnavailable",
  opening_chat: "openingChat",
  select_again_to_retry: "selectAgainToRetry",
  select_again_to_connect: "selectAgainToConnect",
} as const;

/** Archived projects name the project, kept beside the code. */
export const PROJECT_ARCHIVED_REASON = "project_archived";

export type ConversationReadOnlyReason =
  | keyof typeof REASON_KEY
  | typeof PROJECT_ARCHIVED_REASON;

export function conversationReadOnlyReasonText(
  reason: string | null | undefined,
  projectName?: string | null,
): string {
  const normalized = reason?.trim();
  if (!normalized) return translate(libChatMessages, "chatUnavailable");
  if (normalized === PROJECT_ARCHIVED_REASON)
    return translate(libChatMessages, "projectArchived", {
      name: projectName ?? "",
    });
  const key = REASON_KEY[normalized as keyof typeof REASON_KEY];
  return key ? translate(libChatMessages, key) : normalized;
}
